import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export const ReportHeader = ({ icon: Icon, title, subtitle, backRoute, actions = null }) => {
  const navigate = useNavigate();
  return (
    <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
      <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center">
          <Button variant="ghost" onClick={() => navigate(backRoute)} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="report-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Icon className="w-7 h-7 mr-3" />
          <div>
            <h1 className="text-xl font-bold font-display" data-testid="report-title">{title}</h1>
            <p className="text-sm text-[#E8DCC8]">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
    </header>
  );
};

export const StatCard = ({ label, value, icon: Icon, testId, tone = 'text-[#2D5F4F]' }) => (
  <Card>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-[#1A1F2E] tabular-nums" data-testid={testId}>{value}</p>
      </div>
      {Icon && <Icon className={`w-8 h-8 ${tone}`} />}
    </CardContent>
  </Card>
);

export const DateSearchFilters = ({ filters, onChange, onSearch, onClear, loading, placeholder, children = null }) => (
  <Card className="mb-6">
    <CardContent className="py-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <div>
          <label className="text-sm font-medium text-gray-700">Date From</label>
          <Input type="date" value={filters.dateFrom} onChange={(e) => onChange({ dateFrom: e.target.value })} data-testid="filter-date-from" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Date To</label>
          <Input type="date" value={filters.dateTo} onChange={(e) => onChange({ dateTo: e.target.value })} data-testid="filter-date-to" />
        </div>
        <div className={children ? '' : 'md:col-span-2'}>
          <label className="text-sm font-medium text-gray-700">Search (anything)</label>
          <Input placeholder={placeholder} value={filters.search} onChange={(e) => onChange({ search: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()} data-testid="filter-search" />
        </div>
        {children}
        <div className="flex gap-2">
          <Button onClick={onSearch} disabled={loading} className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="search-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />} Search
          </Button>
          <Button variant="outline" onClick={onClear} data-testid="clear-filters-btn"><X className="w-4 h-4" /></Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const Pager = ({ page, pageSize, total, totalPages, loading, onPage, noun }) => (
  <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between items-center gap-3">
    <span className="text-sm text-gray-600" data-testid="page-info">
      {total > 0 ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()} ${noun}` : `No ${noun}`}
    </span>
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => onPage(page - 1)} disabled={loading || page <= 1} data-testid="prev-page-btn">
        <ChevronLeft className="w-4 h-4" /> Prev
      </Button>
      <span className="text-sm text-gray-600" data-testid="page-number">Page {page} / {totalPages}</span>
      <Button variant="outline" size="sm" onClick={() => onPage(page + 1)} disabled={loading || page >= totalPages} data-testid="next-page-btn">
        Next <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

export const UnknownSection = ({ sectionId }) => (
  <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="unknown-section">
    Unknown section “{sectionId}”.
  </div>
);
